import { ShootingSession } from "../../types/ShootingSession";
import { TargetResultDto } from "../../types/TargetScannedDataDTOs";
import { calculateTotalScoreSum } from "../../utils/calculateScore";
import {
  calculateDistance,
  calculateGroupCenterPosition,
  calculateGroupHeight,
  calculateGroupWidth,
  calculateMaxSpread,
  calculateMeanRadius
} from "../../utils/statsCalculation";
import { getSessionDateInputValue } from "../SessionHistory/sessionHistoryUtils";

export interface StatisticsFilters {
  shootingEventId: string;
  dateFrom: string;
  dateTo: string;
  shotCount: string;
}

export interface SessionStatisticSummary {
  session: ShootingSession;
  dateValue: string;
  totalScore: number;
  declaredShots: number;
  scoredShots: number;
  tensCount: number;
  tenEfficiency: number;
  targetCount: number;
  bestTargetScore: number;
  averageScorePerShot: number;
}

export interface BestTargetSummary {
  session: ShootingSession;
  dateValue: string;
  score: number;
  targetIndex: number;
}

export interface ScoreDistributionEntry {
  score: string;
  count: number;
}

export interface GroupingStatistics {
  meanRadius: number;
  maxSpread: number;
  groupCenterOffset: number;
  groupWidth: number;
  groupHeight: number;
}

export type GroupingMetricKey = keyof GroupingStatistics;

export interface GroupingMetricValue {
  session: ShootingSession;
  dateValue: string;
  value: number;
}

export interface GroupingMetricExtremes {
  metric: GroupingMetricKey;
  min: GroupingMetricValue;
  max: GroupingMetricValue;
}

export interface StatisticsSummary {
  sessionSummaries: SessionStatisticSummary[];
  sessionCount: number;
  totalScore: number;
  totalDeclaredShots: number;
  totalScoredShots: number;
  averageSessionScore: number;
  averageScorePerShot: number;
  bestSession: SessionStatisticSummary | null;
  bestTarget: BestTargetSummary | null;
  bestTargetScore: number;
  scoreDistribution: ScoreDistributionEntry[];
  grouping: GroupingStatistics | null;
  groupingExtremes: GroupingMetricExtremes[];
}

export function filterStatisticsSessions(
  sessions: ShootingSession[],
  filters: StatisticsFilters
): ShootingSession[] {
  return sessions
    .filter((session) => {
      if (filters.shootingEventId && session.shootingEventId !== filters.shootingEventId) {
        return false;
      }

      const sessionDate = getSessionDateInputValue(session.sessionDate);
      if (filters.dateFrom && sessionDate < filters.dateFrom) {
        return false;
      }

      if (filters.dateTo && sessionDate > filters.dateTo) {
        return false;
      }

      if (filters.shotCount) {
        const expectedShotCount = Number(filters.shotCount);
        if (!Number.isInteger(expectedShotCount) || expectedShotCount < 0) {
          return false;
        }

        if (getSessionDeclaredShots(session) !== expectedShotCount) {
          return false;
        }
      }

      return true;
    })
    .sort(compareSessionsByDateAscending);
}

export function normalizeScoreLabel(score: string): string {
  return score.toLowerCase() === "10x" ? "10X" : score;
}

export function parseScoreValue(score: string): number {
  const value = parseFloat(score);
  return Number.isFinite(value) ? value : 0;
}

export function calculateStatisticsSummary(
  sessions: ShootingSession[],
  scoreOrder: string[] = []
): StatisticsSummary {
  const sessionSummaries = sessions.map(createSessionStatisticSummary);
  const totalScore = sessionSummaries.reduce((sum, summary) => sum + summary.totalScore, 0);
  const totalDeclaredShots = sessionSummaries.reduce((sum, summary) => sum + summary.declaredShots, 0);
  const totalScoredShots = sessionSummaries.reduce((sum, summary) => sum + summary.scoredShots, 0);
  const bestSession = sessionSummaries.reduce<SessionStatisticSummary | null>((best, summary) => {
    if (!best || summary.totalScore > best.totalScore) {
      return summary;
    }

    return best;
  }, null);
  const bestTarget = findBestTarget(sessions);

  return {
    sessionSummaries,
    sessionCount: sessionSummaries.length,
    totalScore,
    totalDeclaredShots,
    totalScoredShots,
    averageSessionScore: sessionSummaries.length > 0 ? totalScore / sessionSummaries.length : 0,
    averageScorePerShot: totalDeclaredShots > 0 ? totalScore / totalDeclaredShots : 0,
    bestSession,
    bestTarget,
    bestTargetScore: bestTarget?.score ?? 0,
    scoreDistribution: buildScoreDistribution(sessions, scoreOrder),
    grouping: buildGroupingStatistics(sessions),
    groupingExtremes: buildGroupingExtremes(sessions)
  };
}

function createSessionStatisticSummary(session: ShootingSession): SessionStatisticSummary {
  const targetScores = session.targetResults.map(getTargetScore);
  const totalScore = targetScores.reduce((sum, score) => sum + score, 0);
  const declaredShots = getSessionDeclaredShots(session);
  const sessionPoints = getSessionPoints(session);
  const tensCount = sessionPoints.filter(isTenScore).length;

  return {
    session,
    dateValue: getSessionDateInputValue(session.sessionDate),
    totalScore,
    declaredShots,
    scoredShots: sessionPoints.length,
    tensCount,
    tenEfficiency: declaredShots > 0 ? (tensCount / declaredShots) * 100 : 0,
    targetCount: session.targetResults.length,
    bestTargetScore: targetScores.length > 0 ? Math.max(...targetScores) : 0,
    averageScorePerShot: declaredShots > 0 ? totalScore / declaredShots : 0
  };
}

function buildScoreDistribution(
  sessions: ShootingSession[],
  scoreOrder: string[]
): ScoreDistributionEntry[] {
  const normalizedOrder = scoreOrder.map(normalizeScoreLabel);
  const counts = new Map<string, number>(normalizedOrder.map((score) => [score, 0]));

  sessions.forEach((session) => {
    getSessionPoints(session).forEach((point) => {
      const score = normalizeScoreLabel(point);
      counts.set(score, (counts.get(score) ?? 0) + 1);
    });
  });

  const additionalScores = Array.from(counts.keys())
    .filter((score) => !normalizedOrder.includes(score))
    .sort(compareScoreLabelsDescending);
  const orderedScores = [...normalizedOrder, ...additionalScores];

  return orderedScores.map((score) => ({
    score,
    count: counts.get(score) ?? 0
  }));
}

function findBestTarget(sessions: ShootingSession[]): BestTargetSummary | null {
  return sessions.reduce<BestTargetSummary | null>((best, session) => {
    const dateValue = getSessionDateInputValue(session.sessionDate);

    session.targetResults.forEach((targetResult, targetIndex) => {
      const score = getTargetScore(targetResult);
      if (!best || score > best.score) {
        best = {
          session,
          dateValue,
          score,
          targetIndex
        };
      }
    });

    return best;
  }, null);
}

function buildGroupingStatistics(sessions: ShootingSession[]): GroupingStatistics | null {
  const allHits = sessions.flatMap((session) =>
    session.targetResults.flatMap((targetResult) => targetResult.coordinatesOfHitsRelativeToCenter)
  );

  if (allHits.length === 0) {
    return null;
  }

  return calculateGroupingStatistics(allHits);
}

function buildGroupingExtremes(sessions: ShootingSession[]): GroupingMetricExtremes[] {
  const sessionGroupingStatistics = sessions
    .map((session) => {
      const allHits = session.targetResults.flatMap((targetResult) => targetResult.coordinatesOfHitsRelativeToCenter);
      if (allHits.length === 0) {
        return null;
      }

      return {
        session,
        dateValue: getSessionDateInputValue(session.sessionDate),
        grouping: calculateGroupingStatistics(allHits)
      };
    })
    .filter((entry): entry is { session: ShootingSession; dateValue: string; grouping: GroupingStatistics } => entry !== null);

  if (sessionGroupingStatistics.length === 0) {
    return [];
  }

  const metrics: GroupingMetricKey[] = ["meanRadius", "maxSpread", "groupCenterOffset", "groupWidth", "groupHeight"];

  return metrics.map((metric) => {
    const firstEntry = sessionGroupingStatistics[0];
    const initialValue = {
      session: firstEntry.session,
      dateValue: firstEntry.dateValue,
      value: firstEntry.grouping[metric]
    };

    return sessionGroupingStatistics.reduce<GroupingMetricExtremes>((extremes, entry) => {
      const currentValue = {
        session: entry.session,
        dateValue: entry.dateValue,
        value: entry.grouping[metric]
      };

      if (currentValue.value < extremes.min.value) {
        extremes.min = currentValue;
      }

      if (currentValue.value > extremes.max.value) {
        extremes.max = currentValue;
      }

      return extremes;
    }, {
      metric,
      min: initialValue,
      max: initialValue
    });
  });
}

function calculateGroupingStatistics(allHits: { x: number; y: number }[]): GroupingStatistics {
  const groupCenter = calculateGroupCenterPosition(allHits);

  return {
    meanRadius: calculateMeanRadius(allHits),
    maxSpread: calculateMaxSpread(allHits),
    groupCenterOffset: calculateDistance(groupCenter, { x: 0, y: 0 }),
    groupWidth: calculateGroupWidth(allHits),
    groupHeight: calculateGroupHeight(allHits)
  };
}

function getSessionPoints(session: ShootingSession): string[] {
  return session.targetResults.flatMap((targetResult) => targetResult.listOfPointsScored);
}

function getTargetScore(targetResult: TargetResultDto): number {
  return calculateTotalScoreSum(targetResult.listOfPointsScored);
}

function getSessionDeclaredShots(session: ShootingSession): number {
  return session.shotsPerTarget * session.targetResults.length;
}

function isTenScore(score: string): boolean {
  return normalizeScoreLabel(score) === "10X" || score === "10/" || score === "10";
}

function compareSessionsByDateAscending(a: ShootingSession, b: ShootingSession): number {
  const dateDiff = new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }

  return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
}

function compareScoreLabelsDescending(a: string, b: string): number {
  const valueDiff = parseScoreValue(b) - parseScoreValue(a);
  if (valueDiff !== 0) {
    return valueDiff;
  }

  return a.localeCompare(b);
}
